/**
 * TenantUpdates — Library Update Manager.
 *
 * Compares the current library live versions against all sub-tenant
 * forked prompts and shows a confidence-scored impact tree.
 *
 * HIGH confidence (inherited — tenant never customized): one-click
 * "Copy to draft" or bulk "Copy all safe updates".
 *
 * LOW confidence (tenant customized): "Review diff" expands an inline
 * diff showing what the library changed vs what the tenant changed.
 */

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner, formatDate } from "../components.jsx";
import { computeDiff } from "./diff-utils.js";

export default function TenantUpdates() {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({});
  const [applied, setApplied] = useState(new Set());
  const [appliedLog, setAppliedLog] = useState([]); // { sid, agent_key, time }
  const [viewTab, setViewTab] = useState("pending"); // "pending" | "done"

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getLibraryUpdates();
      setImpact(res.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copyToDraft = async (tid, sid, agentKey, kind) => {
    const bk = `${sid}/${agentKey}`;
    setBusy((b) => ({ ...b, [bk]: true }));
    try {
      // `kind` is "chain-framework" for Built-in Agents, omitted for
      // regular specialty agents. The server branches on this to pick
      // writeSubTenantPrompt vs writeSubTenantChainPrompt.
      await api.applyLibraryUpdate(tid, sid, { agent_key: agentKey, ...(kind ? { kind } : {}) });
      setApplied((s) => new Set(s).add(bk));
      setAppliedLog((log) => [...log, { tid, sid, agent_key: agentKey, kind: kind || "agent", time: new Date().toLocaleTimeString() }]);
    } catch (err) {
      setError(err);
    } finally {
      setBusy((b) => ({ ...b, [bk]: false }));
    }
  };

  const copyAllSafe = async (tid, sid, artifacts) => {
    // Bulk endpoint currently only supports specialty agents. Chain-
    // framework artifacts are applied one at a time in parallel so bulk
    // copy still feels like a single action to the operator.
    const pending = artifacts.filter(
      (a) => a.confidence === "high" && !applied.has(`${sid}/${a.agent_key}`)
    );
    if (pending.length === 0) return;
    const specialtyKeys = pending.filter((a) => (a.kind || "agent") === "agent").map((a) => a.agent_key);
    const chainArtifacts = pending.filter((a) => a.kind === "chain-framework");
    const bk = `${sid}/__bulk`;
    setBusy((b) => ({ ...b, [bk]: true }));
    try {
      if (specialtyKeys.length > 0) {
        await api.applyLibraryUpdatesBulk(tid, sid, { agent_keys: specialtyKeys });
      }
      for (const a of chainArtifacts) {
        await api.applyLibraryUpdate(tid, sid, { agent_key: a.agent_key, kind: "chain-framework" });
      }
      const next = new Set(applied);
      const newLogs = [];
      for (const a of pending) {
        next.add(`${sid}/${a.agent_key}`);
        newLogs.push({ tid, sid, agent_key: a.agent_key, kind: a.kind || "agent", time: new Date().toLocaleTimeString() });
      }
      setApplied(next);
      setAppliedLog((log) => [...log, ...newLogs]);
    } catch (err) {
      setError(err);
    } finally {
      setBusy((b) => ({ ...b, [bk]: false }));
    }
  };

  return (
    <>
      <PageHeader
        title="Library Updates"
        subtitle="Compare library live against tenant sub-tenants and apply updates."
        breadcrumbs={[
          { label: "Tenants", to: "/tenants" },
          { label: "Library Updates" },
        ]}
        actions={
          <button className="btn" onClick={load} disabled={loading}>
            {loading ? "Checking..." : "Refresh"}
          </button>
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        {loading ? (
          <Loading label="Analyzing library impact across all tenants..." />
        ) : !impact || impact.total_artifacts === 0 ? (
          <div className="empty-state">
            <h3>All tenants are up to date</h3>
            <p>
              No sub-tenant prompts are behind the current library live
              version. Nothing to update.
            </p>
          </div>
        ) : (
          <>
            {/* Pending / Done tabs */}
            <div className="btn-row mb-16">
              <button
                className={`btn sm ${viewTab === "pending" ? "primary" : ""}`}
                onClick={() => setViewTab("pending")}
              >
                Pending ({impact.total_artifacts - applied.size})
              </button>
              <button
                className={`btn sm ${viewTab === "done" ? "primary" : ""}`}
                onClick={() => setViewTab("done")}
              >
                Done ({applied.size})
              </button>
            </div>

            {viewTab === "done" && (
              <div className="card">
                <div className="card-header">
                  <h3>Applied Updates</h3>
                </div>
                {appliedLog.length === 0 ? (
                  <p className="muted small">No updates applied yet in this session.</p>
                ) : (
                  <table className="table" style={{ border: "none" }}>
                    <thead>
                      <tr>
                        <th>Sub-tenant</th>
                        <th>Agent</th>
                        <th>Applied at</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appliedLog.map((l, i) => (
                        <tr key={i}>
                          <td className="mono small">{l.sid}</td>
                          <td className="mono small">{l.agent_key}</td>
                          <td className="muted small">{l.time}</td>
                          <td><span className="badge badge-live">Copied to draft</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {viewTab === "pending" && (
            <>
            {/* Summary stats */}
            <div className="stat-grid" style={{ marginBottom: 16 }}>
              <div className="card" style={{ textAlign: "center", padding: 16 }}>
                <div className="muted small">Tenants impacted</div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>
                  {impact.total_tenants_impacted}
                </div>
              </div>
              <div className="card" style={{ textAlign: "center", padding: 16 }}>
                <div className="muted small">Total artifacts</div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>
                  {impact.total_artifacts}
                </div>
              </div>
              <div
                className="card"
                style={{
                  textAlign: "center",
                  padding: 16,
                  background: "#f0fff4",
                  borderColor: "#a3e4a5",
                }}
              >
                <div className="muted small">Safe to auto-copy</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#2e7d32" }}>
                  {impact.total_auto_copyable}
                </div>
              </div>
              <div
                className="card"
                style={{
                  textAlign: "center",
                  padding: 16,
                  background: "#fff8f0",
                  borderColor: "#f5c97a",
                }}
              >
                <div className="muted small">Needs review</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#b46e00" }}>
                  {impact.total_needs_review}
                </div>
              </div>
            </div>

            <p className="muted small mb-16">
              Computed at {formatDate(impact.computed_at)}. HIGH confidence
              items can be auto-copied to the sub-tenant's draft. LOW
              confidence items have tenant customizations that need manual
              review.
            </p>

            {/* Per-tenant/sub-tenant impact sections */}
            {impact.impacts.map((imp) => (
              <ImpactSection
                key={imp.sub_tenant_id}
                imp={imp}
                busy={busy}
                applied={applied}
                onCopy={copyToDraft}
                onCopyAllSafe={copyAllSafe}
              />
            ))}
            </>
            )}
          </>
        )}
      </div>
    </>
  );
}

function ImpactSection({ imp, busy, applied, onCopy, onCopyAllSafe }) {
  const highCount = imp.artifacts.filter((a) => a.confidence === "high").length;
  const lowCount = imp.artifacts.filter((a) => a.confidence === "low").length;
  const allHighApplied = imp.artifacts
    .filter((a) => a.confidence === "high")
    .every((a) => applied.has(`${imp.sub_tenant_id}/${a.agent_key}`));

  const [summarizing, setSummarizing] = useState(false);
  const [llmSummary, setLlmSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);

  const runSummarize = async () => {
    setSummarizing(true);
    setSummaryError(null);
    try {
      const res = await api.summarizeChanges(imp.artifacts);
      if (res.data?.summary) {
        setLlmSummary(res.data.summary);
      } else {
        setSummaryError(res.data?.reason || "No summary returned");
      }
    } catch (err) {
      setSummaryError(err.message || String(err));
    } finally {
      setSummarizing(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div
        className="card-header"
        style={{ justifyContent: "space-between" }}
      >
        <div>
          <h3 style={{ margin: 0 }}>
            <Link to={`/tenants/${imp.tenant_id}`}>{imp.tenant_name}</Link>
            {" / "}
            <Link
              to={`/tenants/${imp.tenant_id}/sub-tenants/${imp.sub_tenant_id}/draft`}
            >
              {imp.sub_tenant_name}
            </Link>
          </h3>
          <div className="muted small" style={{ marginTop: 4 }}>
            Agent group: {imp.specialty} ·{" "}
            {highCount > 0 && (
              <span style={{ color: "#2e7d32" }}>
                {highCount} auto-copyable
              </span>
            )}
            {highCount > 0 && lowCount > 0 && " · "}
            {lowCount > 0 && (
              <span style={{ color: "#b46e00" }}>
                {lowCount} needs review
              </span>
            )}
          </div>
          {/* Sub-tenant level summary — only if ≤ 20 changes */}
          {imp.artifacts.length <= 20 && (() => {
            const allChanges = [];
            for (const art of imp.artifacts) {
              if (!art.library_new_content && !art.tenant_current_content) continue;
              const libNew = (art.library_new_content || "").split("\n");
              const tenCur = (art.tenant_current_content || "").split("\n");
              const diff = computeDiff(tenCur, libNew);
              const addedLines = diff.filter((d) => d.type === "added");
              const removedLines = diff.filter((d) => d.type === "removed");
              // Find notable section-level changes
              const sections = addedLines
                .filter((d) => /^[A-Z\[{#═]/.test(d.text.trim()) && d.text.trim().length > 10)
                .map((d) => d.text.trim().slice(0, 50));
              if (addedLines.length > 0 || removedLines.length > 0) {
                allChanges.push({
                  agent: art.agent_name || art.agent_key,
                  added: addedLines.length,
                  removed: removedLines.length,
                  sections: sections.slice(0, 2),
                  confidence: art.confidence,
                });
              }
            }
            if (allChanges.length === 0) return null;
            const totalAdded = allChanges.reduce((s, c) => s + c.added, 0);
            const totalRemoved = allChanges.reduce((s, c) => s + c.removed, 0);
            return (
              <div style={{ marginTop: 6, padding: "6px 10px", background: "#f8f9fa", borderRadius: 4, fontSize: 11 }}>
                <div style={{ fontWeight: 600, marginBottom: 3, color: "#333" }}>
                  Summary: <span style={{ color: "#2e7d32" }}>+{totalAdded}</span> / <span style={{ color: "#c62828" }}>-{totalRemoved}</span> lines across {allChanges.length} agent{allChanges.length !== 1 ? "s" : ""}
                </div>
                {allChanges.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, alignItems: "baseline", padding: "1px 0" }}>
                    <span style={{ fontWeight: 500, minWidth: 100 }}>{c.agent}</span>
                    <span style={{ color: "#2e7d32" }}>+{c.added}</span>
                    <span style={{ color: "#c62828" }}>-{c.removed}</span>
                    <span className={`badge ${c.confidence === "high" ? "badge-live" : "badge-stale"}`} style={{ fontSize: 9, padding: "0 4px" }}>
                      {c.confidence === "high" ? "safe" : "review"}
                    </span>
                    {c.sections.length > 0 && (
                      <span className="muted" style={{ fontSize: 10 }}>({c.sections.join(", ")})</span>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
        {highCount > 0 && !allHighApplied && (
          <button
            className="btn primary sm"
            onClick={() => onCopyAllSafe(imp.tenant_id, imp.sub_tenant_id, imp.artifacts)}
            disabled={!!busy[`${imp.sub_tenant_id}/__bulk`]}
          >
            {busy[`${imp.sub_tenant_id}/__bulk`]
              ? "Copying..."
              : `Copy ${highCount} safe update${highCount !== 1 ? "s" : ""} to draft`}
          </button>
        )}
      </div>

      {/* LLM Summary — on demand */}
      <div style={{ padding: "0 16px 8px", display: "flex", alignItems: "center", gap: 8 }}>
        <button
          className="btn btn-sm"
          onClick={runSummarize}
          disabled={summarizing}
          style={{ fontSize: 11 }}
        >
          {summarizing ? "Summarizing..." : llmSummary ? "Re-summarize" : "Summarize Changes"}
        </button>
        {summaryError && <span style={{ fontSize: 11, color: "#c62828" }}>{summaryError}</span>}
      </div>

      {llmSummary && (
        <div style={{ margin: "0 16px 12px", padding: "10px 14px", background: "#f0f4ff", border: "1px solid #c5cae9", borderRadius: 6, fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: "#283593" }}>{llmSummary.overall}</div>
          {(llmSummary.agents || []).map((ag, i) => (
            <div key={i} style={{ marginBottom: 6, paddingLeft: 8, borderLeft: "3px solid #7986cb" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                <strong>{ag.agent}</strong>
                <span className={`badge ${ag.impact === "high" ? "badge-danger" : ag.impact === "medium" ? "badge-stale" : "badge-neutral"}`} style={{ fontSize: 9, padding: "0 5px" }}>
                  {ag.impact}
                </span>
                <span className="muted" style={{ fontSize: 10 }}>{ag.action?.replace(/-/g, " ")}</span>
              </div>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {(ag.changes || []).map((c, ci) => (
                  <li key={ci} style={{ fontSize: 11, color: "#444", lineHeight: 1.4 }}>{c}</li>
                ))}
              </ul>
            </div>
          ))}
          {(llmSummary.recommendations || []).length > 0 && (
            <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #c5cae9" }}>
              <strong style={{ fontSize: 11 }}>Recommendations:</strong>
              <ul style={{ margin: "2px 0 0 16px", padding: 0 }}>
                {llmSummary.recommendations.map((r, i) => (
                  <li key={i} style={{ fontSize: 11, color: "#333" }}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <table className="table" style={{ border: "none" }}>
        <thead>
          <tr>
            <th>Agent</th>
            <th style={{ width: 120 }}>Confidence</th>
            <th style={{ width: 100 }}>Library v</th>
            <th style={{ width: 100 }}>Tenant v</th>
            <th style={{ width: 140 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {imp.artifacts.map((a) => {
            const bk = `${imp.sub_tenant_id}/${a.agent_key}`;
            const isApplied = applied.has(bk);
            return (
              <ArtifactRow
                key={a.agent_key}
                artifact={a}
                imp={imp}
                isApplied={isApplied}
                busy={busy}
                onCopy={onCopy}
                onApplyMerge={onCopy}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Artifact row with expandable diff/merge for LOW confidence ─────

function ArtifactRow({ artifact: a, imp, isApplied, busy, onCopy, onApplyMerge }) {
  const bk = `${imp.sub_tenant_id}/${a.agent_key}`;
  const [expanded, setExpanded] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergedContent, setMergedContent] = useState(null);
  const [mergeError, setMergeError] = useState(null);
  const [editedMerge, setEditedMerge] = useState(null);
  const [applyBusy, setApplyBusy] = useState(false);

  const runLLMMerge = async () => {
    setMerging(true);
    setMergeError(null);
    try {
      const res = await api.llmMerge({
        library_new: a.library_new_content,
        tenant_current: a.tenant_current_content,
        agent_key: a.agent_key,
        specialty: imp.specialty,
      });
      if (res.data?.merged_content) {
        setMergedContent(res.data.merged_content);
        setEditedMerge(res.data.merged_content);
      } else {
        setMergeError(
          res.data?.reason || "LLM merge returned no content"
        );
      }
    } catch (err) {
      setMergeError(err.message || String(err));
    } finally {
      setMerging(false);
    }
  };

  const acceptMerge = async () => {
    setApplyBusy(true);
    try {
      await api.applyLibraryUpdate(imp.tenant_id, imp.sub_tenant_id, {
        agent_key: a.agent_key,
        content: editedMerge || mergedContent,
        author: "llm-merge",
        ...(a.kind ? { kind: a.kind } : {}),
      });
      // Mark as applied in parent
      onApplyMerge(imp.tenant_id, imp.sub_tenant_id, a.agent_key);
    } catch (err) {
      setMergeError(err.message || String(err));
    } finally {
      setApplyBusy(false);
    }
  };

  // Compute change summary bullets from diff
  const changeSummary = (() => {
    if (!a.library_new_content && !a.tenant_current_content) return null;
    const libNew = (a.library_new_content || "").split("\n");
    const tenCur = (a.tenant_current_content || "").split("\n");
    if (libNew.length === 0 && tenCur.length === 0) return null;

    const diff = computeDiff(tenCur, libNew);
    const added = diff.filter((d) => d.type === "added").length;
    const removed = diff.filter((d) => d.type === "removed").length;

    // Find section headers that changed (lines starting with caps/brackets)
    const addedSections = diff
      .filter((d) => d.type === "added" && /^[A-Z\[{#═]/.test(d.text.trim()))
      .map((d) => d.text.trim().slice(0, 60))
      .slice(0, 5);
    const removedSections = diff
      .filter((d) => d.type === "removed" && /^[A-Z\[{#═]/.test(d.text.trim()))
      .map((d) => d.text.trim().slice(0, 60))
      .slice(0, 5);

    return { added, removed, addedSections, removedSections, charsDelta: a.library_chars_delta };
  })();

  return (
    <>
      <tr>
        <td>
          <strong>{a.agent_name}</strong>
          {a.kind === "chain-framework" && (
            <span
              className="badge"
              style={{ marginLeft: 6, background: "#e3f2fd", color: "#1565c0", fontSize: 10, fontWeight: 600 }}
              title="Platform chain framework prompt (built-in agent)"
            >
              built-in
            </span>
          )}
          <div className="muted small mono">{a.agent_key}</div>
          {/* Change summary bullets */}
          {changeSummary && (
            <div style={{ marginTop: 4, fontSize: 11, color: "#555" }}>
              <span style={{ color: "#2e7d32" }}>+{changeSummary.added}</span>
              {" / "}
              <span style={{ color: "#c62828" }}>-{changeSummary.removed}</span>
              {" lines"}
              {changeSummary.charsDelta != null && (
                <span className="muted"> ({changeSummary.charsDelta > 0 ? "+" : ""}{changeSummary.charsDelta} chars)</span>
              )}
              {changeSummary.addedSections.length > 0 && (
                <ul style={{ margin: "2px 0 0 16px", padding: 0, listStyle: "none" }}>
                  {changeSummary.addedSections.map((s, i) => (
                    <li key={i} style={{ color: "#2e7d32" }}>+ {s}</li>
                  ))}
                </ul>
              )}
              {changeSummary.removedSections.length > 0 && (
                <ul style={{ margin: "2px 0 0 16px", padding: 0, listStyle: "none" }}>
                  {changeSummary.removedSections.map((s, i) => (
                    <li key={i} style={{ color: "#c62828" }}>- {s}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </td>
        <td>
          {a.confidence === "high" ? (
            <span className="badge badge-live">Auto-copy</span>
          ) : (
            <span
              className="badge"
              style={{
                background: "#fff3cd",
                color: "#856404",
                border: "1px solid #ffc107",
              }}
            >
              Needs review
            </span>
          )}
        </td>
        <td className="mono small">
          v{a.library_old_version} → v{a.library_new_version}
        </td>
        <td className="mono small">v{a.tenant_version}</td>
        <td>
          {isApplied ? (
            <span className="badge badge-live">Copied to draft</span>
          ) : a.confidence === "high" ? (
            <button
              className="btn sm"
              onClick={() =>
                onCopy(imp.tenant_id, imp.sub_tenant_id, a.agent_key, a.kind)
              }
              disabled={!!busy[bk]}
            >
              {busy[bk] ? "..." : "Copy to draft"}
            </button>
          ) : (
            <button
              className="btn sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Hide diff" : "Review diff"}
            </button>
          )}
        </td>
      </tr>

      {/* Expandable merge panel for LOW confidence */}
      {expanded && a.confidence === "low" && !isApplied && (
        <tr>
          <td colSpan={5} style={{ padding: 0 }}>
            <InteractiveMerge
              artifact={a}
              imp={imp}
              onAccept={async (content) => {
                setApplyBusy(true);
                try {
                  await api.applyLibraryUpdate(imp.tenant_id, imp.sub_tenant_id, {
                    agent_key: a.agent_key,
                    content,
                    author: "manual-merge",
                    ...(a.kind ? { kind: a.kind } : {}),
                  });
                  onApplyMerge(imp.tenant_id, imp.sub_tenant_id, a.agent_key);
                } catch (err) {
                  setMergeError(err.message);
                } finally {
                  setApplyBusy(false);
                }
              }}
              onLLMMerge={runLLMMerge}
              mergedContent={mergedContent}
              merging={merging}
              mergeError={mergeError}
              applyBusy={applyBusy}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Interactive Merge Tool ──────────────────────────────────────────
// Groups diff into change blocks. Each block can be accepted (use library)
// or rejected (keep tenant). Quick actions at top for bulk operations.

function InteractiveMerge({ artifact: a, imp, onAccept, onLLMMerge, mergedContent, merging, mergeError, applyBusy }) {
  const tenantLines = (a.tenant_current_content || "").split("\n");
  const libraryLines = (a.library_new_content || "").split("\n");
  const diff = computeDiff(tenantLines, libraryLines);

  // Group diff into blocks: consecutive same-type entries form a block
  const blocks = [];
  let currentBlock = null;
  for (const entry of diff) {
    if (!currentBlock || currentBlock.type !== entry.type) {
      if (currentBlock) blocks.push(currentBlock);
      currentBlock = { type: entry.type, lines: [entry] };
    } else {
      currentBlock.lines.push(entry);
    }
  }
  if (currentBlock) blocks.push(currentBlock);

  // Group adjacent removed+added into "change" blocks
  const mergeBlocks = [];
  for (let i = 0; i < blocks.length; i++) {
    if (blocks[i].type === "removed" && i + 1 < blocks.length && blocks[i + 1].type === "added") {
      mergeBlocks.push({ type: "change", removed: blocks[i].lines, added: blocks[i + 1].lines, id: mergeBlocks.length });
      i++; // skip the added block
    } else if (blocks[i].type === "added") {
      mergeBlocks.push({ type: "addition", added: blocks[i].lines, id: mergeBlocks.length });
    } else if (blocks[i].type === "removed") {
      mergeBlocks.push({ type: "deletion", removed: blocks[i].lines, id: mergeBlocks.length });
    } else {
      mergeBlocks.push({ type: "equal", lines: blocks[i].lines, id: mergeBlocks.length });
    }
  }

  const changeBlocks = mergeBlocks.filter((b) => b.type !== "equal");

  // State: which blocks to accept (use library version)
  const [accepted, setAccepted] = useState(() => {
    const m = {};
    changeBlocks.forEach((b) => { m[b.id] = true; }); // default: accept all library changes
    return m;
  });

  const [mergeTab, setMergeTab] = useState("interactive"); // "interactive" | "preview" | "llm"

  const toggleBlock = (id) => setAccepted((prev) => ({ ...prev, [id]: !prev[id] }));
  const acceptAll = () => { const m = {}; changeBlocks.forEach((b) => { m[b.id] = true; }); setAccepted(m); };
  const rejectAll = () => { const m = {}; changeBlocks.forEach((b) => { m[b.id] = false; }); setAccepted(m); };

  // Build merged output from decisions
  const buildMerged = () => {
    const result = [];
    for (const block of mergeBlocks) {
      if (block.type === "equal") {
        result.push(...block.lines.map((l) => l.text));
      } else if (block.type === "change") {
        if (accepted[block.id]) {
          result.push(...block.added.map((l) => l.text)); // use library
        } else {
          result.push(...block.removed.map((l) => l.text)); // keep tenant
        }
      } else if (block.type === "addition") {
        if (accepted[block.id]) {
          result.push(...block.added.map((l) => l.text)); // accept new
        }
        // rejected = skip (don't add)
      } else if (block.type === "deletion") {
        if (!accepted[block.id]) {
          result.push(...block.removed.map((l) => l.text)); // keep removed lines
        }
        // accepted = confirm deletion
      }
    }
    return result.join("\n");
  };

  const acceptedCount = changeBlocks.filter((b) => accepted[b.id]).length;
  const rejectedCount = changeBlocks.length - acceptedCount;

  return (
    <div style={{ padding: 16, background: "#fafafa", borderTop: "1px solid #e1e4e8" }}>
      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
        {[
          { id: "interactive", label: `Manual Merge (${changeBlocks.length} changes)` },
          { id: "preview", label: "Preview Result" },
          { id: "llm", label: "LLM Merge" },
        ].map((t) => (
          <button
            key={t.id}
            className={`btn btn-sm ${mergeTab === t.id ? "primary" : ""}`}
            onClick={() => setMergeTab(t.id)}
            style={{ fontSize: 11 }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {mergeError && <div className="error-banner" style={{ marginBottom: 12 }}>{mergeError}</div>}

      {mergeTab === "interactive" && (
        <>
          {/* Quick actions */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, padding: "6px 10px", background: "#fff", border: "1px solid var(--color-border)", borderRadius: 4 }}>
            <button className="btn btn-sm" onClick={acceptAll} style={{ fontSize: 10, background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" }}>
              Accept all library changes
            </button>
            <button className="btn btn-sm" onClick={rejectAll} style={{ fontSize: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a" }}>
              Keep all tenant versions
            </button>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11 }}>
              <span style={{ color: "#2e7d32", fontWeight: 600 }}>{acceptedCount} accepted</span>
              {" / "}
              <span style={{ color: "#c62828", fontWeight: 600 }}>{rejectedCount} kept</span>
              {" of "}{changeBlocks.length}
            </span>
          </div>

          {/* Change blocks */}
          <div style={{ maxHeight: "50vh", overflow: "auto", border: "1px solid var(--color-border)", borderRadius: 4 }}>
            {mergeBlocks.map((block) => {
              if (block.type === "equal") {
                // Show condensed context (first/last line if more than 3)
                const lines = block.lines;
                if (lines.length <= 3) {
                  return lines.map((l, i) => (
                    <div key={`${block.id}-${i}`} style={{ padding: "1px 8px", fontSize: 11, fontFamily: "var(--font-mono)", color: "#888", background: "#fff" }}>
                      {l.text || "\u00A0"}
                    </div>
                  ));
                }
                return (
                  <div key={block.id} style={{ padding: "2px 8px", fontSize: 10, color: "#aaa", background: "#fff", textAlign: "center" }}>
                    ... {lines.length} unchanged lines ...
                  </div>
                );
              }

              const isAccepted = accepted[block.id];
              const removed = block.removed || [];
              const added = block.added || [];

              return (
                <div
                  key={block.id}
                  style={{
                    borderLeft: `4px solid ${isAccepted ? "#4caf50" : "#ef5350"}`,
                    background: isAccepted ? "#f1f8f1" : "#fef5f5",
                    marginBottom: 1,
                  }}
                >
                  {/* Block header with toggle */}
                  <div
                    onClick={() => toggleBlock(block.id)}
                    style={{
                      padding: "3px 8px", cursor: "pointer", fontSize: 10, fontWeight: 600,
                      color: isAccepted ? "#2e7d32" : "#c62828",
                      display: "flex", alignItems: "center", gap: 6,
                      background: isAccepted ? "#e8f5e9" : "#ffebee",
                    }}
                  >
                    <span>{isAccepted ? "✓ Using library" : "✗ Keeping tenant"}</span>
                    <span className="muted" style={{ fontSize: 9 }}>
                      {block.type === "change" ? `${removed.length} removed, ${added.length} added` :
                       block.type === "addition" ? `${added.length} new lines` :
                       `${removed.length} lines removed`}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 9, color: "#999" }}>click to toggle</span>
                  </div>
                  {/* Show the diff lines */}
                  {removed.map((l, i) => (
                    <div key={`r${i}`} style={{ padding: "1px 8px 1px 20px", fontSize: 11, fontFamily: "var(--font-mono)", background: "#ffeef0", color: "#b31d28", textDecoration: isAccepted ? "line-through" : "none", opacity: isAccepted ? 0.5 : 1 }}>
                      - {l.text}
                    </div>
                  ))}
                  {added.map((l, i) => (
                    <div key={`a${i}`} style={{ padding: "1px 8px 1px 20px", fontSize: 11, fontFamily: "var(--font-mono)", background: "#e6ffec", color: "#22863a", opacity: isAccepted ? 1 : 0.3 }}>
                      + {l.text}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Apply button */}
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button className="btn primary sm" onClick={() => onAccept(buildMerged())} disabled={applyBusy}>
              {applyBusy ? "Applying..." : "Apply merge to draft"}
            </button>
          </div>
        </>
      )}

      {mergeTab === "preview" && (
        <div>
          <textarea
            value={buildMerged()}
            readOnly
            style={{
              width: "100%", minHeight: 300, padding: 8,
              fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.55,
              border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)",
              resize: "vertical", background: "#fff",
            }}
          />
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button className="btn primary sm" onClick={() => onAccept(buildMerged())} disabled={applyBusy}>
              {applyBusy ? "Applying..." : "Apply to draft"}
            </button>
          </div>
        </div>
      )}

      {mergeTab === "llm" && (
        <div>
          {!mergedContent ? (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <button className="btn primary sm" onClick={onLLMMerge} disabled={merging}>
                {merging ? "Merging with LLM..." : "Run LLM Merge"}
              </button>
              <span className="muted small">Uses Gemini to merge library + tenant versions</span>
            </div>
          ) : (
            <MergeResultView
              mergedContent={mergedContent}
              editedMerge={null}
              setEditedMerge={() => {}}
              tenantContent={a.tenant_current_content}
              libraryContent={a.library_new_content}
              onAccept={() => onAccept(mergedContent)}
              onRerun={onLLMMerge}
              onCancel={() => {}}
              applyBusy={applyBusy}
              merging={merging}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Merge result with before/after diff tabs
function MergeResultView({ mergedContent, editedMerge, setEditedMerge, tenantContent, libraryContent, onAccept, onRerun, onCancel, applyBusy, merging }) {
  const [mergeTab, setMergeTab] = useState("diff"); // "diff" | "edit"
  const content = editedMerge ?? mergedContent;

  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
        {[
          { id: "diff", label: "Before / After Diff" },
          { id: "edit", label: "Edit Merged Result" },
        ].map((t) => (
          <button
            key={t.id}
            className={`btn btn-sm ${mergeTab === t.id ? "primary" : ""}`}
            onClick={() => setMergeTab(t.id)}
            style={{ fontSize: 11, padding: "3px 10px" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {mergeTab === "diff" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <h4 style={{ margin: "0 0 6px", fontSize: 12 }}>
              Tenant Current → Merged
              <span className="muted small" style={{ marginLeft: 8 }}>What changed from your version</span>
            </h4>
            <DiffBlock oldContent={tenantContent} newContent={content} label="Tenant → Merged" />
          </div>
          <div>
            <h4 style={{ margin: "0 0 6px", fontSize: 12 }}>
              Library New → Merged
              <span className="muted small" style={{ marginLeft: 8 }}>What changed from library</span>
            </h4>
            <DiffBlock oldContent={libraryContent} newContent={content} label="Library → Merged" />
          </div>
        </div>
      )}

      {mergeTab === "edit" && (
        <textarea
          value={content}
          onChange={(e) => setEditedMerge(e.target.value)}
          style={{
            width: "100%", minHeight: 300, padding: 8,
            fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.55,
            border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)",
            resize: "vertical",
          }}
        />
      )}

      <div className="btn-row" style={{ marginTop: 12 }}>
        <button className="btn primary sm" onClick={onAccept} disabled={applyBusy}>
          {applyBusy ? "Applying..." : "Accept & copy to draft"}
        </button>
        <button className="btn sm" onClick={onRerun} disabled={merging}>
          {merging ? "Re-merging..." : "Re-run merge"}
        </button>
        <button className="btn sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// Compact inline diff block used in the 3-way diff view.
function DiffBlock({ oldContent, newContent, label }) {
  const oldLines = (oldContent || "").split("\n");
  const newLines = (newContent || "").split("\n");
  const diff = computeDiff(oldLines, newLines);

  // Only show changed lines + 2 lines of context
  const changed = [];
  for (let i = 0; i < diff.length; i++) {
    if (diff[i].type !== "equal") {
      const start = Math.max(0, i - 2);
      const end = Math.min(diff.length, i + 3);
      for (let j = start; j < end; j++) {
        if (!changed.includes(j)) changed.push(j);
      }
    }
  }

  if (changed.length === 0) {
    return (
      <div className="muted small" style={{ padding: 8 }}>
        No differences
      </div>
    );
  }

  return (
    <div
      style={{
        maxHeight: 300,
        overflow: "auto",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-sm)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        lineHeight: 1.5,
      }}
    >
      {changed.map((idx) => {
        const line = diff[idx];
        if (!line) return null;
        const bg =
          line.type === "removed"
            ? "#fdd"
            : line.type === "added"
            ? "#dfd"
            : "transparent";
        const prefix =
          line.type === "removed" ? "−" : line.type === "added" ? "+" : " ";
        return (
          <div
            key={idx}
            style={{
              padding: "0 6px",
              background: bg,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <span style={{ color: "#999", marginRight: 6 }}>{prefix}</span>
            {line.text}
          </div>
        );
      })}
    </div>
  );
}
